// admin/routes/RoutesList.jsx
// ==========================================
// Admin Routes List with comprehensive filtering
// Includes: Tabs, Search filters, Multi-select, Dual range slider
// ==========================================

import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";
import { fetchAdminRoutes, fetchAdminRouteDetails, clearAuditNav } from "@/store/slices/adminSlice";

import Table from "../../common/Table";
import Pagination from "../../common/Pagination";

import MultiSelect from "../../common/MultiSelect";
import FilterPanel from "../../common/FilterPanel";
import DualRangeSlider from "../../common/DualRangeSlider";
import Button from "../../common/Button";
import RouteDetailDrawer from "./RouteDetailDrawer";
import RouteApprovalModal from "./RouteApprovalModal";
import AdminRouteFormModal from "./AdminRouteFormModal";
import { INDIAN_STATES } from "@/utils/constants";

const RoutesList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // ==========================================
  // REDUX STATE
  // ==========================================
  const { routes, totalRoutes, routesPage, routesTotalPages, routesLimit, isLoading, error, currentRoute } = useSelector(
    (state) => state.admin
  );

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  // Selected route for drawer/modal
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [approvalRoute, setApprovalRoute] = useState(null);
  const [addRouteModalOpen, setAddRouteModalOpen] = useState(false);

  // ─── Audit Log Deep-Link: auto-open drawer when ?id= is in URL ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkId = params.get('id');
    if (deepLinkId) {
      navigate(location.pathname, { replace: true });
      dispatch(fetchAdminRouteDetails(deepLinkId));
    }
    return () => { dispatch(clearAuditNav()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When currentRoute is set from the by-ID fetch, open its drawer
  useEffect(() => {
    if (currentRoute && currentRoute._id) {
      setSelectedRoute(currentRoute);
    }
  }, [currentRoute]);

  // Calculate max distance from API routes or use default
  const maxDistance = useMemo(() => {
    if (routes.length === 0) return 200; // Default max
    const distances = routes.map((r) => r.distance || 0);
    return Math.ceil(Math.max(...distances, 200)); // Ensure at least 200km
  }, [routes]);

  // Filter states
  const [filters, setFilters] = useState({
    name: "", // Name search
    status: ["pending"], // Status filter (multi-select) - Default to "pending" only
    states: [], // Selected states (multi-select)
    distance: [0, 200], // Distance range [min, max] - Will update when maxDistance changes
    creator: "", // Creator search
    difficulty: [], // Selected difficulty levels (multi-select)
  });

  // Update distance filter when maxDistance changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      distance: [0, maxDistance],
    }));
  }, [maxDistance]);

  // Sorting states
  const [sorting, setSorting] = useState({
    date: "recent", // "recent" (default) or "oldest"
    routeName: null, // null (not sorted), "asc" (A-Z), or "desc" (Z-A)
    distance: null, // null (not sorted), "asc" (Low to High), or "desc" (High to Low)
  });

  // ==========================================
  // API INTEGRATION
  // ==========================================

  // Map sorting to API parameters
  const getSortParams = useMemo(() => {
    if (sorting.date !== null) {
      // For date sorting, use sort=recent or sort=oldest (no order param)
      return {
        sort: sorting.date, // "recent" or "oldest"
      };
    } else if (sorting.routeName !== null) {
      // For name sorting, use sort=name with order
      return {
        sort: "name",
        order: sorting.routeName, // "asc" or "desc"
      };
    } else if (sorting.distance !== null) {
      // For distance sorting, use sort=distance with order
      return {
        sort: "distance",
        order: sorting.distance, // "asc" or "desc"
      };
    }
    return { sort: "recent" }; // Default
  }, [sorting]);

  // ==========================================
  // COMPUTED VALUES
  // ==========================================

  // Get unique states from INDIAN_STATES (excluding the default "Select a State")
  const stateOptions = useMemo(() => {
    return INDIAN_STATES.filter((s) => s.code !== "") // Exclude the default option
      .map((s) => ({ value: s.name, label: s.name }));
  }, []);

  // Map state names to codes for API
  const getStateCodes = useMemo(() => {
    return (stateNames) => {
      if (!stateNames || stateNames.length === 0) return [];
      return stateNames
        .map((name) => {
          const state = INDIAN_STATES.find((s) => s.name === name);
          return state?.code;
        })
        .filter(Boolean); // Remove any undefined values
    };
  }, []);

  // ==========================================
  // API FETCH EFFECT
  // ==========================================

  // Fetch routes when filters or sorting changes
  const fetchRoutes = React.useCallback(
    (page = 1) => {
      const apiFilters = {
        page,
        limit: routesLimit,

        // Status filter
        status: filters.status.length > 0 ? filters.status : undefined,

        // State filter - map state names to codes
        state_code:
          filters.states.length > 0 ? getStateCodes(filters.states) : undefined,

        // Distance range
        minDistance: filters.distance[0],
        maxDistance: filters.distance[1],

        // Creator search
        creator: filters.creator.trim() || undefined,

        // Name search
        name: filters.name.trim() || undefined,

        // Difficulty filter
        difficulty:
          filters.difficulty.length > 0 ? filters.difficulty : undefined,

        // Sorting
        ...getSortParams,
      };

      // Remove undefined values
      Object.keys(apiFilters).forEach((key) => {
        if (apiFilters[key] === undefined) {
          delete apiFilters[key];
        }
      });

      dispatch(fetchAdminRoutes(apiFilters));
    },
    [dispatch, routesLimit, filters, getSortParams, getStateCodes]
  );

  useEffect(() => {
    fetchRoutes(1);
  }, [fetchRoutes]);

  const handlePageChange = (newPage) => {
    fetchRoutes(newPage);
  };

  // ==========================================
  // HANDLERS
  // ==========================================

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      name: "",
      status: ["pending"], // Reset to default "pending" only
      states: [],
      distance: [0, maxDistance],
      creator: "",
      difficulty: [],
    });
  };

  // Check if any filters are active (non-default)
  const hasActiveFilters =
    filters.name.trim() !== "" ||
    !(filters.status.length === 1 && filters.status[0] === "pending") ||
    filters.states.length > 0 ||
    filters.distance[0] !== 0 ||
    filters.distance[1] !== maxDistance ||
    filters.creator.trim() !== "" ||
    filters.difficulty.length > 0;

  // Sorting handlers (UI only - functionality to be added by backend)
  // Only ONE sorting option can be active at a time, others are null ("Not Sorted")
  // At least one sorting must always be active (cannot have all three as "Not Sorted")
  const handleSortToggle = (type) => {
    setSorting((prev) => {
      // Reset all sorting to null (not sorted) first
      const newSorting = {
        date: null,
        routeName: null,
        distance: null,
      };

      switch (type) {
        case "date":
          // Toggle between: recent <-> oldest
          // If date was already active, toggle its state
          // If another sorting was active, activate date with default (recent)
          if (prev.date === "recent") {
            newSorting.date = "oldest";
          } else if (prev.date === "oldest") {
            newSorting.date = "recent";
          } else {
            // Date was null, activate it with default
            newSorting.date = "recent";
          }
          break;
        case "routeName":
          // Toggle between: asc <-> desc
          // If routeName was already active, toggle its state
          // If another sorting was active, activate routeName with default (asc)
          if (prev.routeName === "asc") {
            newSorting.routeName = "desc";
          } else if (prev.routeName === "desc") {
            newSorting.routeName = "asc";
          } else {
            // routeName was null, activate it with default
            newSorting.routeName = "asc";
          }
          break;
        case "distance":
          // Toggle between: asc <-> desc
          // If distance was already active, toggle its state
          // If another sorting was active, activate distance with default (asc)
          if (prev.distance === "asc") {
            newSorting.distance = "desc";
          } else if (prev.distance === "desc") {
            newSorting.distance = "asc";
          } else {
            // distance was null, activate it with default
            newSorting.distance = "asc";
          }
          break;
      }

      return newSorting;
    });
  };

  // Check if any sorting is active (non-default)
  // Since one sorting is always active, check if it's not the default date sort
  const hasActiveSorting =
    sorting.date === "oldest" ||
    sorting.routeName !== null ||
    sorting.distance !== null;

  // Reset all sorting to default (date: recent, others: null)
  const handleClearSorting = () => {
    setSorting({
      date: "recent",
      routeName: null,
      distance: null,
    });
  };

  // Handle action button click
  const handleAction = (route) => {
    if (route.status === "pending") {
      setApprovalRoute(route);
    } else {
      setSelectedRoute(route);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Routes Management
        </h2>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setAddRouteModalOpen(true)}
        >
          Add Route
        </Button>
      </div>

      {/* Filters Section - Now using FilterPanel component */}
      <FilterPanel
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Status Filter (Multi-Select) */}
          <div>
            <MultiSelect
              label="Status"
              options={[
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "hidden", label: "Hidden" },
              ]}
              selected={filters.status}
              onChange={(values) => setFilters({ ...filters, status: values })}
              placeholder="Select status..."
            />
          </div>

          {/* Name Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Route Name
            </label>
            <input
              type="text"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              placeholder="Search by name..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* State Filter (Multi-Select) */}
          <div>
            <MultiSelect
              label="States"
              options={stateOptions}
              selected={filters.states}
              onChange={(values) => setFilters({ ...filters, states: values })}
              placeholder="Select states..."
            />
          </div>

          {/* Difficulty Filter (Multi-Select) */}
          <div>
            <MultiSelect
              label="Difficulty"
              options={[
                { value: "Easy", label: "Easy" },
                { value: "Medium", label: "Medium" },
                { value: "Hard", label: "Hard" },
              ]}
              selected={filters.difficulty}
              onChange={(values) =>
                setFilters({ ...filters, difficulty: values })
              }
              placeholder="Select difficulty..."
            />
          </div>

          {/* Distance Filter (Dual Range Slider) */}
          <div>
            <DualRangeSlider
              label="Distance Range"
              min={0}
              max={maxDistance}
              value={filters.distance}
              onChange={(values) =>
                setFilters({ ...filters, distance: values })
              }
              unit="km"
              step={1}
            />
          </div>

          {/* Creator Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Creator
            </label>
            <input
              type="text"
              value={filters.creator}
              onChange={(e) =>
                setFilters({ ...filters, creator: e.target.value })
              }
              placeholder="Search by username..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </FilterPanel>

      {/* Sorting Section - Using FilterPanel component */}
      {/* Note: Clear button removed from sorting panel as sorting can be reset by cycling through options */}
      <FilterPanel
        title="Sorting"
        icon={ArrowUpDown}
        hasActiveFilters={false} // Disable clear button for sorting
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date Sorting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date
            </label>
            <button
              onClick={() => handleSortToggle("date")}
              className={`w-full px-4 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                sorting.date === null
                  ? "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                  : sorting.date === "recent"
                    ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                    : "bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400"
              } hover:shadow-md`}
            >
              {sorting.date === null
                ? "Not Sorted"
                : sorting.date === "recent"
                  ? "Recent to Oldest ↓"
                  : "Oldest to Recent ↑"}
            </button>
          </div>

          {/* Route Name Sorting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Route Name
            </label>
            <button
              onClick={() => handleSortToggle("routeName")}
              className={`w-full px-4 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                sorting.routeName === null
                  ? "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                  : sorting.routeName === "asc"
                    ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                    : "bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400"
              } hover:shadow-md`}
            >
              {sorting.routeName === null
                ? "Not Sorted"
                : sorting.routeName === "asc"
                  ? "A → Z"
                  : "Z → A"}
            </button>
          </div>

          {/* Distance Sorting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Distance
            </label>
            <button
              onClick={() => handleSortToggle("distance")}
              className={`w-full px-4 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                sorting.distance === null
                  ? "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                  : sorting.distance === "asc"
                    ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                    : "bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400"
              } hover:shadow-md`}
            >
              {sorting.distance === null
                ? "Not Sorted"
                : sorting.distance === "asc"
                  ? "Low → High"
                  : "High → Low"}
            </button>
          </div>
        </div>
      </FilterPanel>

      {/* Results Count */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {isLoading ? (
          "Loading routes..."
        ) : error ? (
          <span className="text-red-600 dark:text-red-400">Error: {error}</span>
        ) : (
          <>
            Showing {routes.length} of {totalRoutes} routes
          </>
        )}
      </div>

      {/* Routes Table */}
      <Table
        columns={[
          { key: "name", title: "Name" },
          { key: "state", title: "State" },
          { key: "distance", title: "Distance" },
          { key: "creator", title: "Creator" },
          { key: "status", title: "Status" },
          { key: "actions", title: "Actions" },
        ]}
        data={routes}
        renderRow={(route) => (
          <tr
            key={route._id}
            className="hover:bg-gray-50 dark:hover:bg-gray-900"
          >
            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
              {route.name}
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
              {route.state_code || route.state}
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
              {route.distance} km
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
              {route.creator_id?.name || route.creator?.name || "N/A"}
            </td>
            <td className="px-4 py-3">
              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  route.status === "pending"
                    ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                    : route.status === "approved"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : route.status === "hidden"
                        ? "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                }`}
              >
                {route.status}
              </span>
            </td>
            <td className="px-4 py-3">
              <button
                onClick={() => handleAction(route)}
                className={`text-sm font-medium ${
                  route.status === "pending"
                    ? "text-green-600 hover:text-green-700 dark:text-green-400"
                    : "text-blue-600 hover:text-blue-700 dark:text-blue-400"
                }`}
              >
                {route.status === "pending" ? "Open Approval" : "View"}
              </button>
            </td>
          </tr>
        )}
      />

      <Pagination
        page={routesPage}
        totalPages={routesTotalPages}
        onChange={handlePageChange}
      />

      {/* Modals/Drawers */}
      {selectedRoute && (
        <RouteDetailDrawer
          route={selectedRoute}
          onClose={() => setSelectedRoute(null)}
        />
      )}

      {approvalRoute && (
        <RouteApprovalModal
          route={approvalRoute}
          onClose={() => setApprovalRoute(null)}
        />
      )}

      {/* Add Route Modal */}
      <AdminRouteFormModal
        open={addRouteModalOpen}
        onClose={() => setAddRouteModalOpen(false)}
      />
    </div>
  );
};

export default RoutesList;
